import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    Button,
    List,
    ListItem,
    ListItemText,
    Typography,
    ListItemButton,
    Divider
} from "@mui/material";

export interface PartOption {
    id: string;
    aliasName: string;
    partId: string;
    partNumber: string;
    uom: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    parts: PartOption[];
    selectedPartIds: string[]; // <-- passed from parent
    onSubmit: (parts: PartOption[]) => void;
}

const AddPartPopup: React.FC<Props> = ({
    open,
    onClose,
    parts,
    selectedPartIds,
    onSubmit
}) => {
    const [localSelection, setLocalSelection] = useState<string[]>([]);

    // When popup opens, reset selection
    useEffect(() => {
        if (open) setLocalSelection([]);
    }, [open]);

    const toggle = (id: string) => {
        if (selectedPartIds.includes(id)) return; // disable toggling
        setLocalSelection((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const selectableParts = parts.filter(
        (p) => !selectedPartIds.includes(p.id)
    );

    const isAllSelected =
        selectableParts.length > 0 &&
        localSelection.length === selectableParts.length;
    const isIndeterminate =
        localSelection.length > 0 &&
        localSelection.length < selectableParts.length;

    const handleSelectAll = () => {
        if (isAllSelected) {
            setLocalSelection([]);
        } else {
            setLocalSelection(selectableParts.map((t) => t.id));
        }
    };

    const handleSubmit = () => {
        const selected = parts.filter((p) => localSelection.includes(p.id));
        onSubmit(selected);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Select Parts</DialogTitle>

            <DialogContent sx={{ maxHeight: 400, overflowY: "auto" }}>
                <List>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={handleSelectAll}
                            disabled={selectableParts.length === 0}
                        >
                            <Checkbox
                                checked={isAllSelected}
                                indeterminate={isIndeterminate}
                                disabled={selectableParts.length === 0}
                            />
                            <ListItemText primary="Select All" />
                        </ListItemButton>
                    </ListItem>
                    <Divider />

                    {parts.map((p) => {
                        const alreadySelected = selectedPartIds.includes(p.id);
                        const checked = localSelection.includes(p.id);

                        return (
                            <ListItem
                                key={p.id}
                                sx={{ opacity: alreadySelected ? 0.8 : 1 }}
                            >
                                <ListItemButton
                                    disabled={alreadySelected}
                                    onClick={() => toggle(p.id)}
                                >
                                    <Checkbox
                                        checked={checked}
                                        disabled={alreadySelected}
                                    />

                                    <ListItemText
                                        primary={
                                            <Typography fontWeight={600}>
                                                {p.aliasName}
                                            </Typography>
                                        }
                                        secondary={`${p.partId} • ${p.partNumber}`}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="error">
                    CANCEL
                </Button>
                <Button
                    color="primary"
                    disabled={localSelection.length === 0}
                    onClick={handleSubmit}
                >
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddPartPopup;
